import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import { RadioGroup, Box, Slider, FormControlLabel } from "@mui/material";
import { translations } from "../translations/translations";
import { useLanguage } from "../context/LanguageContext";

export interface QuestionProps {
  id: number;
  type: string;
  question: string;
  options?: string[];
  answer?: string | number;
  explanation?: string;
  min?: number;
  max?: number;
}

interface QuizProps {
  questions: QuestionProps[];
  onChange?: (answers: Record<number, string | number>) => void;
}

const Quiz: React.FC<QuizProps> = ({ questions, onChange }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string | number>>({});
  const [disabled, setDisabled] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  const [sliderValue, setSliderValue] = useState<number | null>(null);

  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    if (currentQuestion.type === "slider") {
      const existing = answers[currentQuestion.id];
      if (typeof existing === "number") {
        setSliderValue(existing);
      } else {
        setSliderValue(currentQuestion.min ?? 0);
      }
    } else {
      setSliderValue(null);
    }
  }, [currentQuestionIndex, currentQuestion, answers]);

  const handleAnswer = (value: string | number) => {
    if (disabled) return;

    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));
    setDisabled(true);
    setTimeout(() => {
      setDisabled(false);
      nextQuestion();
    }, 1500);
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      setIsFinished(true);
      onChange?.(answers);
    }
  };

  if (isFinished) {
    return (
      <div
        style={{
          maxWidth: 500,
          fontFamily: "Lato",
          maxHeight: "100%",
          overflowY: "auto",
          padding: 16,
        }}
      >
        <Typography variant="h5" sx={{ mb: 1, fontSize: "30px" }}>
          Results
        </Typography>
        <Typography variant="body1" sx={{ mb: 1, fontSize: "18px" }}>
          {questions.map((q) => {
            const userAnswer = answers[q.id];
            const isCorrect = userAnswer === q.answer;

            return (
              <div key={q.id} style={{ marginBottom: 16 }}>
                <p><strong>{q.question}</strong></p>
                <p>
                  Your Answer:{" "}
                  <span style={{ color: isCorrect ? "green" : "red" }}>
                    {String(userAnswer)}
                  </span>
                </p>

                {!isCorrect && q.answer !== undefined && (
                  <p>
                    Right answer was:{" "}
                    <strong style={{ color: "green" }}>
                      {String(q.answer)}
                    </strong>
                  </p>
                )}

                {q.explanation && (
                  <p style={{ fontStyle: "italic", marginTop: 8 }}>
                    {q.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </Typography>
      </div>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="h5" sx={{ mb: 1, fontSize: "30px" }}>
        Question {currentQuestionIndex + 1} / {questions.length}
      </Typography>

      <Typography variant="body1" sx={{ mb: 2, fontSize: "25px" }}>
        {currentQuestion.question}
      </Typography>

      {/* YES/NO */}
      {currentQuestion.type === "yes-no" && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <Button
              variant={
                answers[currentQuestion.id] === "JA"
                  ? "contained"
                  : "outlined"
              }
              color="primary"
              onClick={() => handleAnswer("JA")}
              disabled={disabled}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: 999,
                py: 1.5,
                fontSize: "18px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow:
                  answers[currentQuestion.id] === "JA"
                    ? "0 8px 20px rgba(0,0,0,0.15)"
                    : "none",
                opacity: disabled ? 0.7 : 1,
              }}
            >
              {t.quiz.yes}
            </Button>

            <Button
              variant={
                answers[currentQuestion.id] === "NEIN"
                  ? "contained"
                  : "outlined"
              }
              color="primary"
              onClick={() => handleAnswer("NEIN")}
              disabled={disabled}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: 999,
                py: 1.5,
                fontSize: "18px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow:
                  answers[currentQuestion.id] === "NEIN"
                    ? "0 8px 20px rgba(0,0,0,0.15)"
                    : "none",
                opacity: disabled ? 0.7 : 1,
              }}
            >
              {t.quiz.no}
            </Button>
          </Box>
        </Box>
      )}


      {/* Multiple Choice */}
      {currentQuestion.type === "multiple-choice" && (
        <RadioGroup
          value={answers[currentQuestion.id] ?? ""}
          onChange={(e) => handleAnswer(e.target.value)}
        >
          {currentQuestion.options?.map((opt) => (
            <FormControlLabel
              key={opt}
              value={opt}
              control={<Radio />}
              label={opt}
              sx={{ fontSize: "25px" }}
            />
          ))}
        </RadioGroup>
      )}

      {/* Slider */}
      {currentQuestion.type === "slider" && (
        <Box sx={{ mb: 2 }}>
          <Slider
            value={
              sliderValue !== null
                ? sliderValue
                : currentQuestion.min ?? 0
            }
            min={currentQuestion.min ?? 0}
            max={currentQuestion.max ?? 100}
            onChange={(_, val) => setSliderValue(val as number)}
            valueLabelDisplay="auto"
            disabled={disabled}
          />

          <Typography sx={{ mt: 1, fontSize: "18px" }}>
            {t.quiz.yourChoice}{" "}
            <strong>
              {sliderValue !== null
                ? sliderValue
                : currentQuestion.min ?? 0}
            </strong>
          </Typography>

          <Button
            variant={sliderValue !== null ? "contained" : "outlined"}
            color="primary"
            onClick={() => sliderValue !== null && handleAnswer(sliderValue)}
            disabled={disabled || sliderValue === null}
            sx={{
              mt: 3,
              width: "100%",
              borderRadius: 999,
              py: 1.7,
              fontSize: "18px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow:
                sliderValue !== null
                  ? "0 8px 20px rgba(0,0,0,0.15)"
                  : "none",
              opacity: disabled ? 0.7 : 1,
              transition: "0.25s",
            }}
          >
            {t.quiz.confirm}
          </Button>

        </Box>
      )}
    </Box>
  );
};

export default Quiz;
